import React from 'react';
import { Box, ButtonBase, MenuItem, TextField, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { tok, LABEL } from './clockUi';
import { groupLabel } from './clockScope';
import { DEPTH_FILTERS, toggleDepth } from './clockDepthFilter';
import { STAGE_FILTERS, toggleStage } from './clockStageFilter';
import { SIDES } from './clockSide';

// The Cycle Clock's filters (DWLF-548): one summary line beside the title, and the chip rows behind a
// Filters toggle, so the dial and the read pane open above the fold. Collapsed unless the user opened it.

// Which markets the clock shows (DWLF-509). The watchlist is the default; a group or every tracked market
// shows windows on markets the user does not watch, without adding them to the watchlist.
const SCOPES = [
  { id: 'watchlist', label: 'Watchlist' },
  { id: 'group', label: 'Group' },
  { id: 'tracked', label: 'All tracked' },
];

const toggleSx = (on) => ({
  ...LABEL, height: 36, px: 1.5, fontSize: '0.6875rem', border: `1px solid ${on ? tok('accent') : tok('ink-15')}`,
  backgroundColor: on ? tok('accent') : 'transparent', color: on ? tok('on-accent') : tok('ink-75'),
});

// The scope switch (DWLF-509): Watchlist | Group | All tracked, and the group picker when Group is on.
function ScopeSwitch({ scope, groups, onScope, onGroup }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
      <Typography sx={{ ...LABEL, fontSize: '0.625rem', color: tok('ink-55'), minWidth: 44 }}>Show</Typography>
      <Box role="group" aria-label="Which markets" sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {SCOPES.map((o) => {
          const on = o.id === scope.kind;
          const disabled = o.id === 'group' && !groups?.length;
          return (
            <ButtonBase key={o.id} aria-pressed={on} disabled={disabled} onClick={() => onScope(o.id)} sx={{ ...toggleSx(on), opacity: disabled ? 0.5 : 1 }}>
              {o.label}
            </ButtonBase>
          );
        })}
      </Box>
      {scope.kind === 'group' && groups?.length > 0 && (
        <TextField
          select
          size="small"
          label="Group"
          value={groups.some((g) => g.name === scope.group) ? scope.group : ''}
          onChange={(e) => onGroup(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {groups.map((g) => (
            <MenuItem key={g.name} value={g.name}>{groupLabel(g.name)}</MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  );
}

// Lows | Highs (DWLF-552): which weekly pivot the clock times. A mode, not a filter, so it stays beside the
// summary line rather than behind the Filters toggle.
export function SideSwitch({ side, onSide }) {
  return (
    <Box role="group" aria-label="Cycle pivot" sx={{ display: 'flex', gap: 0.75 }}>
      {Object.values(SIDES).map((o) => (
        <ButtonBase key={o.id} aria-pressed={o.id === side.id} onClick={() => onSide(o.id)} sx={toggleSx(o.id === side.id)}>
          {o.label}
        </ButtonBase>
      ))}
    </Box>
  );
}

// A multi-select chip row (stage, DWLF-547; depth, DWLF-548): chips tick on and off in any combination and
// All clears them. Counts are of the on-dial markets the other filters keep.
function ChipFilter({ label, groupName, options, selected, counts, onChange, toggle }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
      <Typography sx={{ ...LABEL, fontSize: '0.625rem', color: tok('ink-55'), minWidth: 44 }}>{label}</Typography>
      <Box role="group" aria-label={groupName} sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        <ButtonBase aria-pressed={!selected.length} onClick={() => onChange([])} sx={toggleSx(!selected.length)}>
          All ({counts.all})
        </ButtonBase>
        {options.map((f) => {
          const on = selected.includes(f.id);
          return (
            <ButtonBase key={f.id} aria-pressed={on} title={f.title} onClick={() => onChange(toggle(selected, f.id))} sx={toggleSx(on)}>
              {f.label} ({counts[f.id]})
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

const scopeWords = (scope) => {
  if (scope.kind === 'group') return `Group: ${groupLabel(scope.group)}`;
  return SCOPES.find((o) => o.id === scope.kind)?.label ?? 'Watchlist';
};

const chipWords = (options, selected, word = (f) => f.label) => (
  selected.length ? options.filter((f) => selected.includes(f.id)).map(word).join(', ') : 'All'
);

/** The filters in words: "Highs · All tracked · Stage: Early, Late · Depth: IN ZONE, Deep · 8 of 117 on the dial". */
export const describeFilters = ({ side, scope, stages, depths, shown, total }) => [
  side.label,
  scopeWords(scope),
  `Stage: ${chipWords(STAGE_FILTERS, stages, (f) => f.short)}`,
  `Depth: ${chipWords(DEPTH_FILTERS, depths)}`,
  `${shown} of ${total} on the dial`,
].join(' · ');

const PANEL_ID = 'cycle-clock-filters';

export function FilterSummary({ summary, open, onToggle }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.75rem', color: tok('ink-60') }}>{summary}</Typography>
      <ButtonBase aria-expanded={open} aria-controls={PANEL_ID} onClick={onToggle} sx={{ ...toggleSx(open), gap: 0.5, pr: 1 }}>
        Filters {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </ButtonBase>
    </Box>
  );
}

export function FilterPanel({ scope, stage, depth }) {
  return (
    <Box id={PANEL_ID} sx={{ mb: 1, pb: 0.5, borderBottom: `1px solid ${tok('ink-08')}` }}>
      <ScopeSwitch {...scope} />
      <ChipFilter label="Stage" groupName="Filter by stage" options={STAGE_FILTERS} toggle={toggleStage} selected={stage.stages} counts={stage.counts} onChange={stage.onStages} />
      <ChipFilter label="Depth" groupName="Filter by depth" options={DEPTH_FILTERS} toggle={toggleDepth} selected={depth.depths} counts={depth.counts} onChange={depth.onDepths} />
    </Box>
  );
}
